import React, { useState, useEffect, useRef } from 'react';
import { useMount, useUpdateEffect, useToggle } from 'ahooks';
import loading from './img/loading.svg';
import error from './img/error.svg';
import { Modal, Popover } from 'antd';

// Image属性
export interface ImageItem {
    // 图片地址
    src: string;
    // 缩略图地址（不传则采用原图）
    thumbnail?: string;
    className?: string;
    // 是否悬浮弹窗
    isPopover?: boolean;
    // 是否可点击
    isClick?: boolean;
    // 默认cover
    fit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down' | 'inherit' | 'initial' | 'revert' | 'unset' | string;
    [key: string]: any;
}

export const Image: React.FC<ImageItem> = (props) => {
    const { src, className = '', isClick = true, fit, thumbnail, isPopover = true, ...rest } = props;
    if (!src) {
        return <div className={className}>-</div>
    }
    const thumbnailImg = thumbnail || src;
    const [isModalOpen, { toggle }] = useToggle();
    const styles = fit ? { objectFit: fit as React.CSSProperties['objectFit'] } : {}
    const [status, setStatus] = useState<'init' | 'loading' | 'error' | 'success'>('init');
    const ref = useRef<HTMLImageElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    useMount(() => {
        if (!ref.current) return;
        // 创建IntersectionObserver实例
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setStatus('loading');
                    // 在设置loading状态后，立即断开观察器以取消监听
                    observer.disconnect();
                    observerRef.current = null;
                }
            });
        }, { threshold: 0.1 });
        observerRef.current = observer;
        observer.observe(ref.current);

        // 清理函数
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    });
    useUpdateEffect(() => {
        if (status === 'loading') {
            const img = new window.Image();
            img.onload = () => {
                setStatus('success');
            };
            img.onerror = () => {
                setStatus('error');
            };
            img.src = src;
        }
    }, [status])
    // 悬浮
    const imgContainer = isPopover ? <Popover
        trigger="hover"
        placement="right"
        content={<Image className='w-[400px] h-[400px] border-0' fit="contain" isPopover={false} isClick={false} src={src} />}
        destroyOnHidden
    >
        <img onClick={toggle} src={thumbnailImg} className='w-full h-full object-cover' style={styles} {...rest} />
    </Popover> : <div className='w-full h-full flex justify-center items-center'><img className='w-full h-full object-contain' src={src} /></div>
    return <>
        <div className={`border rounded-[8px] overflow-hidden ${className}`} ref={ref}>
            {status === 'loading' && <div className='w-full h-full flex justify-center items-center bg-white'>
                <img className='w-[36px] h-[36px] animate-[spin_2s_linear_infinite]' src={loading}></img>
            </div>}
            {status === 'error' && <div className='w-full h-full flex justify-center items-center bg-[#F5F5F5]'>
                <img className='w-[36px] h-[36px]' src={error}></img>
            </div>}
            {status === 'success' && imgContainer}
        </div>
        <Modal
            width={600}
            open={isModalOpen}
            destroyOnHidden
            footer={false}
            onCancel={toggle}
        >
            <div className='w-full h-[600px] flex justify-center items-center'>
                <Image className='w-full h-full border-0' fit="contain" isPopover={false} isClick={false} src={src} />
            </div>
        </Modal>
    </>
}